import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, RouterModule } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { EmptyStateComponent } from '../../../../../../../../_shared/components/states/empty-state/empty-state.component';
import { ChecklistTableComponent } from '../../../../../../_shared/components/checklist-table/checklist-table.component';
import {
  ChecklistTableActionEvent,
  ChecklistTableColumn,
  ChecklistTableGroup,
  ChecklistTableSectionConfig
} from '../../../../../../_shared/components/checklist-table/checklist-table.model';
import { DocumentViewModalComponent } from '../../../../../../_shared/components/modals/document-view-modal/document-view-modal.component';
import { LinkDocumentTypeModalComponent } from '../../../../../../_shared/components/modals/link-document-type-modal/link-document-type-modal.component';
import { LinkDocumentTypeResult, LinkedDocumentTypeUsage } from '../../../../../../_shared/models/link-document-type.model';
import { CompliancesLibraryService } from '../../../../../../compliance-library/services/compliances-library.service';
import { buildLinkedDocumentExpiryParts, buildLinkedDocumentLabel } from '../../../../../../_shared/helpers/linked-document.helper';
import { LinkedDocumentService } from '../../../../../../_shared/services/linked-document.service';
import { TranslocoService } from '@jsverse/transloco';
import { ChecklistTypeIdEnum, getChecklistTypeOption } from '../../../../../../_shared/models/checklist-type.model';
import {
  InspectionChecklistRow,
  RoutineChecklistRequirementResult,
  RoutineChecklistRequirementResultAttachment,
  RoutineChecklistRequirementResultsRequest
} from '../../../../../models/inspection-checklist.model';
import { ComplianceOverviewService } from '../../../../../services/compliance-overview.service';
import { ErrorHandlerService } from '../../../../../../../../_shared/services/error-handler.service';

@Component({
  selector: 'app-inspection-checklist',
  standalone: true,
  imports: [
    RouterModule,
    ChecklistTableComponent,
    EmptyStateComponent
  ],
  templateUrl: './inspection-checklist.component.html',
  styleUrl: './inspection-checklist.component.scss'
})
export class InspectionChecklistComponent implements OnInit, OnDestroy {
  private route: ActivatedRoute = inject(ActivatedRoute);
  private complianceOverviewService: ComplianceOverviewService = inject(ComplianceOverviewService);
  private compliancesLibraryService: CompliancesLibraryService = inject(CompliancesLibraryService);
  private linkedDocumentService: LinkedDocumentService = inject(LinkedDocumentService);
  private errorService: ErrorHandlerService = inject(ErrorHandlerService);
  private modalService: NgbModal = inject(NgbModal);
  private toastr: ToastrService = inject(ToastrService);
  private transloco: TranslocoService = inject(TranslocoService);

  private readonly statusLabelKeys: Record<string, string> = {
    Pending: 'compliance.shared.results.pending',
    Pass: 'compliance.shared.results.pass',
    Fail: 'compliance.shared.results.fail'
  };
  private readonly cancelSubject = new Subject<void>();

  isLoading: boolean = true;
  hasError: boolean = false;
  checklist: string = '';
  assetName: string = '';
  tab: string = '';
  submissionId: number = 0;
  checklistId: number = 0;
  assetId: number = 0;
  complianceId: number = 0;
  checklistTypeId: ChecklistTypeIdEnum = ChecklistTypeIdEnum.Inspection;
  backLink: string[] = ['/asset-management/compliance-overview/asset-routine-compliance-details'];
  backQueryParams: Record<string, string | number> = {};

  checklistSections: ChecklistTableSectionConfig[] = [];
  checklistGroups: ChecklistTableGroup<InspectionChecklistRow>[] = [];

  get hasNoResults(): boolean {
    return this.checklistGroups.length === 0
      || this.checklistGroups.every(group => group.rows.length === 0);
  }

  onActionClicked(event: ChecklistTableActionEvent<InspectionChecklistRow>): void {
    if (event.actionName === 'attach-document') {
      void this.openAttachDocumentModalAsync(event.row);
      return;
    }

    if (event.actionName === 'view-linked-document') {
      void this.viewLinkedDocumentAsync(event.row);
      return;
    }

    if (event.actionName !== 'view-document' || !event.row.documentUrl) {
      return;
    }

    const modalRef = this.modalService.open(DocumentViewModalComponent, {
      centered: true,
      size: 'lg'
    });

    const reference = event.row.attachmentReference;
    modalRef.componentInstance.title = this.transloco.translate('compliance.overview.inspectionChecklist.attachment');
    modalRef.componentInstance.documentName = event.row.documentsLabel;
    modalRef.componentInstance.documentUrl = event.row.documentUrl;

    if (reference) {
      modalRef.componentInstance.downloadHandler = async () => {
        const response = await this.complianceOverviewService.getChecklistAttachmentDownloadLinkAsync(
          this.submissionId,
          this.checklistId,
          reference
        );
        return response.data ?? null;
      };
    }
  }

  private async viewLinkedDocumentAsync(row: InspectionChecklistRow): Promise<void> {
    if (!row.linkedDocument) {
      return;
    }

    await this.linkedDocumentService.openAsync(this.assetId, row.linkedDocument, this.cancelSubject);
  }

  private async openAttachDocumentModalAsync(row: InspectionChecklistRow): Promise<void> {
    const modalRef = this.modalService.open(LinkDocumentTypeModalComponent, {
      centered: true,
      windowClass: 'link-document-type-modal-window'
    });
    modalRef.componentInstance.requirementName = row.requirementName;
    modalRef.componentInstance.usedTypes = await this.getUsedTypesAsync(row);

    modalRef.result.then(
      (result: LinkDocumentTypeResult) => void this.linkDocumentTypeAsync(row, result),
      () => {
        // Dismissed.
      }
    );
  }

  private async getUsedTypesAsync(current: InspectionChecklistRow): Promise<LinkedDocumentTypeUsage[]> {
    if (!this.complianceId) {
      return [];
    }

    try {
      const response = await this.complianceOverviewService.getRequirementDocumentLinksAsync(this.complianceId);
      return (response.data ?? [])
        .filter(link => link.requirementId !== current.requirementId)
        .map(link => ({ documentTypeId: link.documentTypeId, requirementName: link.requirementName }));
    } catch {
      return [];
    }
  }

  private async linkDocumentTypeAsync(row: InspectionChecklistRow, result: LinkDocumentTypeResult): Promise<void> {
    try {
      await this.compliancesLibraryService.updateChecklistRequirementDetailsAsync({
        requirementId: row.requirementId,
        complianceId: this.complianceId,
        name: row.requirementName,
        documentTypeId: result.documentTypeId,
        // null = leave the requirement's critical flag untouched; this page doesn't know it.
        isCritical: null
      });

      this.toastr.success(this.transloco.translate('compliance.overview.inspectionChecklist.toasts.documentTypeLinked'));
      await this.loadResultsAsync();
    } catch (error) {
      this.errorService.handleError(error, { showToast: true });
    }
  }

  ngOnInit(): void {
    const map = this.route.snapshot.queryParamMap;
    this.checklist = map.get('checklist') || '';
    this.assetName = map.get('assetName') || '';
    this.tab = map.get('tab') || '';
    this.submissionId = this.parsePositiveInt(map.get('submissionId')) ?? 0;
    this.checklistId = this.parsePositiveInt(map.get('checklistId')) ?? 0;
    this.assetId = this.parsePositiveInt(map.get('assetId')) ?? 0;
    this.complianceId = this.parsePositiveInt(map.get('complianceId')) ?? 0;
    const submissionKind =
      (map.get('checklistTypeName') ?? map.get('checklistType') ?? '').trim();
    this.checklistTypeId =
      getChecklistTypeOption(submissionKind)?.id ?? ChecklistTypeIdEnum.Inspection;

    if (map.get('source') === 'periodic-details') {
      this.backLink = ['/asset-management/compliance-overview/asset-periodic-compliance-details'];
      this.backQueryParams = this.buildPeriodicBackLinkParams(map);
    } else {
      this.backQueryParams = this.buildBackLinkParams(map);
    }
    this.checklistSections = [this.buildSection()];

    void this.fetchRoutineComplianceSubmissionsIfApplicable();
    void this.loadResultsAsync();
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  async loadResultsAsync(): Promise<void> {
    this.isLoading = true;
    this.hasError = false;

    try {
      const response = await this.complianceOverviewService.getRoutineChecklistRequirementResultsAsync(
        this.buildRequest(),
        this.cancelSubject
      );
      const results = response.data ?? [];

      this.checklistGroups = [this.buildGroup(results)];
      this.isLoading = false;
    } catch (error) {
      this.errorService.handleError(error, {
        callback: () => {
          this.hasError = true;
          this.isLoading = false;
        }
      });
    }
  }

  /**
   * Loads checklist submissions for this asset/task when opened from a submission row,
   * so the submissions endpoint runs after drilling into a checklist.
   */
  private async fetchRoutineComplianceSubmissionsIfApplicable(): Promise<void> {
    const q = this.route.snapshot.queryParamMap;
    const assetId = this.parsePositiveInt(q.get('assetId'));
    const routineTypeId = this.parsePositiveInt(q.get('routineTypeId'));
    const routineComplianceTaskId = this.parsePositiveInt(q.get('routineComplianceTaskId'));

    if (!assetId || !routineTypeId || !routineComplianceTaskId) {
      return;
    }

    try {
      await this.complianceOverviewService.getRoutineComplianceChecklistAsync(
        {
          assetId,
          routineTypeId,
          routineComplianceTaskId,
          page: 1,
          pageSize: 50,
        },
        this.cancelSubject
      );
    } catch {
      // Submissions call is best-effort; primary data comes from the requirement-results endpoint.
    }
  }

  private buildRequest(): RoutineChecklistRequirementResultsRequest {
    return {
      submissionId: this.submissionId,
      checklistId: this.checklistId,
      assetId: this.assetId
    };
  }

  private buildSection(): ChecklistTableSectionConfig {
    const isDocument = this.checklistTypeId === ChecklistTypeIdEnum.Document;

    const linkedDocumentColumns: ChecklistTableColumn[] = isDocument
      ? [{
          header: this.transloco.translate('compliance.overview.inspectionChecklist.columns.linkedDocument'),
          value: 'linkedDocumentLabel',
          type: 'attach',
          linkUrlValue: 'linkedDocumentId',
          subLabelValue: 'linkedDocumentExpiry',
          actionName: 'view-linked-document',
          actionLabel: this.transloco.translate('compliance.overview.inspectionChecklist.viewLinkedDocument'),
          attachActionName: 'attach-document',
          attachLabel: this.transloco.translate('compliance.overview.inspectionChecklist.attach'),
          attachUnavailableValue: 'linkedTypeWithoutDocument',
          attachUnavailableLabel: this.transloco.translate('compliance.overview.inspectionChecklist.noDocument'),
          width: '1.5fr'
        }]
      : [];

    return {
      type: this.checklistTypeId,
      title: this.transloco.translate(isDocument
        ? 'compliance.overview.inspectionChecklist.documentChecklist'
        : 'compliance.overview.inspectionChecklist.inspectionChecklist'),
      columns: [
        { header: this.transloco.translate('compliance.overview.inspectionChecklist.columns.requirement'), value: 'requirementName', width: '2fr' },
        ...linkedDocumentColumns,
        {
          header: this.transloco.translate('compliance.overview.inspectionChecklist.columns.inspectionAttachment'),
          value: 'documentsLabel',
          type: 'link',
          linkUrlValue: 'documentUrl',
          actionName: 'view-document',
          actionLabel: this.transloco.translate('compliance.overview.inspectionChecklist.viewAttachment'),
          mutedValue: this.transloco.translate('compliance.overview.inspectionChecklist.noAttachment'),
          width: '1fr'
        },
        { header: this.transloco.translate('compliance.overview.inspectionChecklist.columns.notes'), value: 'notesLabel', width: '2fr', mutedValue: this.transloco.translate('compliance.overview.inspectionChecklist.noNoteProvided') },
        {
          header: this.transloco.translate('compliance.overview.inspectionChecklist.columns.taskStatus'),
          value: 'status',
          labelValue: 'statusLabel',
          type: 'status',
          width: '1fr'
        }
      ]
    };
  }

  private buildGroup(results: RoutineChecklistRequirementResult[]): ChecklistTableGroup<InspectionChecklistRow> {
    return {
      id: this.checklistId,
      name: this.checklist,
      type: this.checklistTypeId,
      rows: results.map(result => this.mapRow(result))
    };
  }

  private mapRow(result: RoutineChecklistRequirementResult): InspectionChecklistRow {
    const firstAttachment = result.attachments?.[0];
    const linkedDocument = result.linkedDocument;
    return {
      requirementId: result.requirementId,
      requirementName: result.requirementName,
      documentsLabel: firstAttachment ? this.buildDocumentLabel(firstAttachment) : this.transloco.translate('compliance.overview.inspectionChecklist.noAttachment'),
      documentUrl: firstAttachment?.link ?? null,
      attachmentReference: firstAttachment?.reference ?? null,
      linkedDocumentId: linkedDocument?.documentId ?? null,
      linkedDocument,
      linkedDocumentLabel: buildLinkedDocumentLabel(linkedDocument),
      linkedDocumentExpiry: this.buildLinkedDocumentExpiryLabel(linkedDocument?.validUntil),
      linkedTypeWithoutDocument: result.linkedDocumentTypeId !== null && !linkedDocument,
      notesLabel: result.notes?.trim() || this.transloco.translate('compliance.overview.inspectionChecklist.noNoteProvided'),
      status: result.status,
      statusLabel: this.buildStatusLabel(result.status)
    };
  }

  private buildStatusLabel(status: unknown): string {
    const labelKey = this.statusLabelKeys[`${status}`];
    return labelKey ? this.transloco.translate(labelKey) : '';
  }

  private buildLinkedDocumentExpiryLabel(validUntil: string | Date | null | undefined): string | null {
    const parts = buildLinkedDocumentExpiryParts(validUntil);
    return parts ? this.transloco.translate(parts.key, { date: parts.date }) : null;
  }

  private buildDocumentLabel(attachment: RoutineChecklistRequirementResultAttachment): string {
    const requirementNumber = (attachment.filename ?? '').split('-')[1] ?? '';
    const extension = attachment.extension ?? '';

    const uploadDate = attachment.uploadedAt?.split('T')[0] ?? '';
    const uploadTime = attachment.uploadedAt?.split('T')[1]?.slice(0, 5) ?? '';

    return `${requirementNumber}/${uploadDate}/${uploadTime}${extension}`;
  }

  private parsePositiveInt(value: string | null): number | null {
    if (value == null || value === '') {
      return null;
    }
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  private buildPeriodicBackLinkParams(map: ParamMap): Record<string, string | number> {
    const out: Record<string, string | number> = {
      assetName: map.get('assetName') || this.assetName,
    };
    const assetId = this.parsePositiveInt(map.get('assetId'));
    const complianceId = this.parsePositiveInt(map.get('complianceId'));
    const taskId = this.parsePositiveInt(map.get('taskId'));

    if (assetId != null) {
      out['assetId'] = assetId;
    }
    if (complianceId != null) {
      out['complianceId'] = complianceId;
    }
    if (taskId != null) {
      out['id'] = taskId;
    }
    return out;
  }

  private buildBackLinkParams(map: ParamMap): Record<string, string | number> {
    const out: Record<string, string | number> = {
      tab: map.get('tab') || this.tab,
      assetName: map.get('assetName') || this.assetName,
    };
    const numericKeys = ['assetId', 'complianceId', 'routineTypeId', 'routineComplianceTaskId'] as const;
    for (const key of numericKeys) {
      const v = this.parsePositiveInt(map.get(key));
      if (v != null) {
        out[key] = v;
      }
    }
    const checklistType = map.get('checklistType');
    if (checklistType) {
      out['checklistType'] = checklistType;
    }
    return out;
  }
}